import React, { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Camera, PlayCircle, Youtube, X } from "lucide-react";

import { Button, ConfidenceBar, OptionRow } from "@/components/kit";
import { FIELDS, typeFromPattern } from "@/lib/options";
import { prepareUpload } from "@/lib/media";

export function ProfileEditor({ profile, onChange, fields, estimates }) {
  const set = (key, v) => {
    const next = { ...profile, [key]: v };
    if (key === "hair_pattern") next.hair_type = typeFromPattern(v) ?? profile.hair_type ?? null;
    if (key === "hair_type" && profile.hair_pattern && typeFromPattern(profile.hair_pattern) !== v) next.hair_pattern = null;
    if (estimates?.[key] && next.confidences) {
      const conf = { ...next.confidences };
      if (v !== estimates[key]?.value) delete conf[key];
      next.confidences = conf;
    }
    onChange(next);
  };
  return (
    <div className="flex flex-col gap-6">
      {fields.map((key) => {
        const f = FIELDS[key];
        const est = estimates?.[key];
        const value = profile[key];
        const overridden = est?.value && value !== est.value;
        return (
          <fieldset key={key} data-testid={`profile-field-${key}`} className="flex flex-col gap-2">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <legend className="t-label">{f.label}{est ? " · AI estimate" : ""}</legend>
                {overridden ? <span className="t-caption">Edited by you</span> : null}
              </div>
              {est && est.value ? <ConfidenceBar testId={`confidence-${key}`} value={est.confidence} /> : null}
              {est && !est.value ? <p className="t-caption">The AI couldn&apos;t estimate this — pick one if you know it.</p> : null}
              {f.help ? <p className="t-caption">{f.help}</p> : null}
            </div>
            <OptionRow testPrefix={`option-${key}`} options={f.options} value={value} multi={f.multi} onChange={(v) => set(key, v)} />
          </fieldset>
        );
      })}
    </div>
  );
}

const EMBED = /(youtube\.com|youtu\.be|vimeo\.com)/i;
function embedUrl(url) {
  const yt = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
  if (/youtu/i.test(url) && yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

/** Tutorial video: uploaded files play in an HTML5 player; YouTube/Vimeo links are embedded. */
export function TutorialVideo({ url }) {
  if (!url) {
    return (
      <div data-testid="style-video-slot" className="mt-6 flex h-[150px] flex-col items-center justify-center gap-1.5 rounded-[20px] border-[1.5px] border-dashed border-sky bg-tert">
        <PlayCircle className="h-9 w-9 text-sky" aria-hidden />
        <span className="t-caption">Video tutorial coming soon</span>
      </div>
    );
  }
  if (EMBED.test(url)) {
    const src = embedUrl(url);
    if (!src) {
      return (
        <a data-testid="style-video-link" href={url} target="_blank" rel="noopener noreferrer" className="focus-ring mt-6 flex h-[150px] flex-col items-center justify-center gap-1.5 rounded-[20px] bg-navy text-white">
          <Youtube className="h-9 w-9" aria-hidden />
          <span className="t-strong !text-white">Watch the video tutorial</span>
        </a>
      );
    }
    return (
      <div data-testid="style-video-embed" className="mx-auto mt-6 aspect-video w-full max-w-[720px] overflow-hidden rounded-[20px] bg-navy">
        <iframe title="Video tutorial" src={src} className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
      </div>
    );
  }
  return (
    <div data-testid="style-video-player" className="mx-auto mt-6 aspect-video w-full max-w-[720px] overflow-hidden rounded-[20px] bg-navy">
      <video src={url} controls preload="metadata" className="h-full w-full object-contain" />
    </div>
  );
}

/**
 * Webcam capture dialog (getUserMedia). Calls onResult({status: picked|cancelled|blocked|error, ...}).
 */
export function CameraDialog({ open, front = true, onResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    doneRef.current = false;
    setReady(false);
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        finish({ status: "error", message: "This browser can't open the camera. Upload a photo instead." });
        return;
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: front ? "user" : "environment", width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false });
        if (cancelled) return s.getTracks().forEach((t) => t.stop());
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        if (e?.name === "NotAllowedError" || e?.name === "SecurityError") finish({ status: "blocked" });
        else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") finish({ status: "error", message: "No camera was found on this device. Upload a photo instead." });
        else if (e?.name === "NotReadableError") finish({ status: "error", message: "Your camera is being used by another app. Close it and try again, or upload a photo." });
        else finish({ status: "error", message: "Couldn't open the camera. Upload a photo instead." });
      }
    })();
    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function stop() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }
  function finish(r) {
    if (doneRef.current) return;
    doneRef.current = true;
    stop();
    onResult(r);
  }
  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    if (front) {
      ctx.translate(c.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(v, 0, 0);
    try {
      finish({ status: "picked", ...(await prepareUpload(c)) });
    } catch (e) {
      finish({ status: "error", message: e.message });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && finish({ status: "cancelled" })}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[rgba(17,24,39,0.7)] animate-fade-in" />
        <Dialog.Content data-testid="camera-dialog" className="fixed left-1/2 top-1/2 z-50 flex w-[min(94vw,560px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-[20px] bg-navy p-4 outline-none">
          <div className="flex items-center justify-between">
            <Dialog.Title className="font-display-bold text-lg text-white">Take a selfie</Dialog.Title>
            <Dialog.Close data-testid="camera-close" aria-label="Close camera" className="focus-ring flex h-11 w-11 items-center justify-center rounded-full bg-glass-dark text-white">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="t-caption !text-soft">Face the camera in soft, even light with your hair visible.</Dialog.Description>
          <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl bg-black">
            <video ref={videoRef} data-testid="camera-video" playsInline muted className={`h-full w-full object-cover ${front ? "-scale-x-100" : ""}`} />
            {!ready ? <p className="absolute inset-0 flex items-center justify-center p-6 text-center t-body !text-white">Waiting for camera access… allow it in the browser prompt.</p> : null}
          </div>
          <Button testId="camera-capture" title="Capture" icon={Camera} variant="secondary" disabled={!ready} onClick={capture} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Hook: returns [pickCamera(front) => Promise<result>, dialogElement]. */
export function useCamera() {
  const [state, setState] = useState(null);
  const pick = (front = true) => new Promise((resolve) => setState({ front, resolve }));
  const el = (
    <CameraDialog
      open={!!state}
      front={state?.front ?? true}
      onResult={(r) => {
        state?.resolve(r);
        setState(null);
      }}
    />
  );
  return [pick, el];
}
